import { useState, type HTMLAttributes, type ReactNode } from 'react';
import { useTribalContext } from '@/context/tribal';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import { isMobile } from '@/modules/utils/breakpoints';
import DropdownIcon from '../DropdownIcon/DropdownIcon';
import PriceBreakdownTable, {
  type PriceBreakdownTableProps,
} from '../PriceBreakdownTable/PriceBreakdownTable';
import {
  DropdownWrapper,
  IconWrapper,
  PriceViewWrapper,
  StyledStockPill,
  VehicleCardBodyWrapper,
  VehicleCardContentWrapper,
  VehicleCardWrapper,
} from './VehicleCard.styles';
import VehicleCardCar from './VehicleCardCar';
import VehicleCardCarNotAvailable from './VehicleCardCarNotAvailable';
import VehicleCardDropdownText from './VehicleCardDropdownText';
import VehicleCardMobilePriceOverlay from './VehicleCardMobilePriceOverlay';

export interface VehicleCardLink {
  url: string;
  text: ReactNode;
  target?: any;
}

export interface VehicleCardProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  totalPrice: string;
  totalPriceLabel: string;
  carSummary: string;
  p11dPrice: string;
  image: {
    url: string;
    alt: string;
  };
  cta: {
    url: string;
    text: string;
    onClick: () => void;
  };
  priceBreakdown?: PriceBreakdownTableProps;
  disclaimerText?: ReactNode;
  disabled?: boolean;
  disabledMessage?: string;
  collapsed?: boolean;
  pdfLink?: VehicleCardLink;
  configuratorLink?: VehicleCardLink;
  stockTitle?: string;
  stockDescription?: string;
}

function VehicleCard(props: VehicleCardProps) {
  const {
    title,
    totalPrice,
    totalPriceLabel,
    p11dPrice,
    carSummary,
    image,
    cta,
    priceBreakdown,
    disclaimerText,
    disabled,
    collapsed,
    disabledMessage,
    pdfLink,
    configuratorLink,
    stockTitle,
    stockDescription,
    ...restProps
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const isMobileWidth = useResizeResponsive(isMobile);
  const { contentManager } = useTribalContext();

  const vehicleCardDropdownTextProps = {
    isMobileWidth,
    totalPrice,
    totalPriceLabel,
    title,
    carSummary,
    disabled,
    p11dPrice,
  };

  return (
    <VehicleCardWrapper>
      {!collapsed && stockDescription && (
        <StyledStockPill
          modalProps={{
            title: stockTitle || '',
            description: stockDescription,
          }}
        >
          {contentManager.get('stock-badge-label')}
        </StyledStockPill>
      )}
      <VehicleCardContentWrapper {...restProps}>
        <DropdownWrapper
          disabled={disabled}
          collapsed={collapsed}
          onClick={() => setIsOpen(!isOpen)}
        >
          <VehicleCardDropdownText
            {...vehicleCardDropdownTextProps}
            summarisedView
          />
          {!collapsed && (
            <IconWrapper disabled={disabled}>
              <DropdownIcon isOpen={isOpen} />
            </IconWrapper>
          )}
        </DropdownWrapper>
        <VehicleCardBodyWrapper collapsed={collapsed}>
          <VehicleCardCar
            disabled={disabled}
            isMobileWidth={isMobileWidth}
            collapsed={collapsed}
            carSummary={carSummary}
            disabledMessage={disabledMessage}
            image={image}
            cta={cta}
          />
          {!collapsed && !disabled && priceBreakdown && (
            <PriceViewWrapper isOpen={isOpen}>
              <PriceBreakdownTable {...priceBreakdown} />
            </PriceViewWrapper>
          )}
          {!collapsed && disabled && (
            <VehicleCardCarNotAvailable
              pdfLink={pdfLink}
              configuratorLink={configuratorLink}
            />
          )}
        </VehicleCardBodyWrapper>
        {!collapsed && isMobileWidth && priceBreakdown && isOpen && (
          <VehicleCardMobilePriceOverlay
            {...vehicleCardDropdownTextProps}
            onClose={() => setIsOpen(false)}
            priceBreakdown={priceBreakdown}
            disclaimerText={disclaimerText}
          />
        )}
      </VehicleCardContentWrapper>
    </VehicleCardWrapper>
  );
}

export default VehicleCard;
